import type { ChatTransportConfig } from '../integrations/chat-transport-config';
import type { Context } from '../integrations/context-types';
import type { A2UIEnvelope } from '../meta/types';

export interface ConversationSummary {
  id: string;
  title: string;
  updatedAt?: string;
  /** Agent that owns this conversation. The shell drawer renders across
   *  agents and uses this to group conversations and to atomically switch
   *  agent on conversation open. */
  agentId: string;
}

export interface ConversationsPage {
  conversations: ConversationSummary[];
  nextCursor: string | null;
}

/** One message in a loaded conversation's history. The shape mirrors AI SDK
 *  v5 UIMessage so `useChat({ initialMessages })` can consume it directly.
 *  Tool / reasoning parts are not reconstructed from disk (V1 parity), but
 *  feedback rating, action cards, sources, and the sources summary survive
 *  as `data-*` parts so historical messages render the same affordances as
 *  live ones. */
export interface ConversationHistoryMessage {
  id: string;
  role: 'user' | 'assistant';
  parts: Array<{ type: string; text?: string; data?: Record<string, unknown> }>;
}

export interface Conversation {
  id: string;
  title: string;
  messages: ConversationHistoryMessage[];
}

export interface Agent {
  id: string;
  name: string;
  description?: string;
  iconUrl?: string;
  /** Host-defined icon identifier (e.g. a key into a sprite map). Forwarded
   *  to `renderAgentAvatar` so the host can pick the right asset. */
  icon?: string;
  /** Host-defined classification (e.g. 'system' vs 'custom'). Forwarded to
   *  `renderAgentAvatar` so the host can pick a different render path. */
  category?: string;
}

/** Lightweight reference to a known conversation, indexed in the shell
 *  state. Distinct from `Conversation` (full payload) and
 *  `ConversationSummary` (server response shape). */
export interface ConversationRef {
  id: string;
  agentId: string;
  title: string;
  updatedAt: string;
}

export interface UploadedFile {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  extractedText?: string;
  /** Server-side storage path the upload endpoint emits (e.g. an S3 key).
   *  Forwarded as `DocumentContext.s3Path` so the backend can resolve the
   *  attachment from context-only payloads. */
  s3Path?: string;
  /** Backend-assigned conversationId when the upload kicked off a fresh
   *  thread (V1 parity). The library binds the active selection to this
   *  id so the next chat send goes to the same conversation the upload
   *  was attached to. null / undefined on uploads to existing threads. */
  conversationId?: string | null;
}

/** The one-shot ratings a user can leave on an assistant message. `brilliant`
 *  is the "exceptionally good answer" signal — stronger than `up`. */
export type FeedbackRating = 'up' | 'down' | 'brilliant';

/** Every rating literal, for runtime guards (hydration, parsing). Kept next
 *  to the type so the two cannot drift apart. */
export const FEEDBACK_RATINGS: readonly FeedbackRating[] = [
  'up',
  'down',
  'brilliant',
];

export function isFeedbackRating(value: unknown): value is FeedbackRating {
  return (FEEDBACK_RATINGS as readonly unknown[]).includes(value);
}

export interface FeedbackPayload {
  messageId: string;
  /** `null` represents the withdraw gesture. Backend treats it as
   *  "no rating." (Not reachable from the current one-shot UI.) */
  rating: FeedbackRating | null;
  comment?: string;
}

export interface ActionExecutionPayload {
  messageId: string;
  actionId: string;
  payload: unknown;
}

/** Input for `Transport.executeSurfaceAction` — the channel A2UI surfaces use
 *  to fire user gestures back to the BE. `correlationId` is the envelope's
 *  surface correlationId (so the BE can route the action to the matching
 *  surface); `surfaceType` + `action` form the dispatch key. */
export interface SurfaceActionInput {
  correlationId: string;
  messageId: string;
  surfaceId: string;
  surfaceType: string;
  /** The dispatching component's own id (e.g. the QuoteOption's `option-2`).
   *  Lets a handler target the exact live component — the Quote Note attaches
   *  to the option that generated. Sourced from the A2UI action's
   *  `sourceComponentId`. */
  sourceComponentId?: string;
  action: string;
  payload: Record<string, unknown>;
}

export interface ActionResult {
  status: 'ok' | 'error';
  data?: unknown;
  error?: string;
}

export interface SerializedContext {
  kind: 'screen' | 'entity' | 'document' | 'selection';
  data: unknown;
}

/** Single starter suggestion shown on the empty chat screen. `icon` is a
 *  host-defined identifier; the library forwards it to `renderStarterIcon`
 *  so the host picks the actual glyph. */
export interface Starter {
  id: string;
  label: string;
  icon: string;
}

/** Context that the server uses to tailor the starter list per-view /
 *  per-entity. All fields are optional — `agentId` is the only required
 *  input. */
export interface ListStartersInput {
  agentId: string;
  view?: string;
  subTab?: string;
  entityType?: string;
  entityId?: string;
  entityName?: string;
}

export interface ListStartersResponse {
  starters: Starter[];
}

export interface SlashCommand {
  readonly name: string;
  readonly description: string;
  readonly skillId: string;
  readonly argHint?: string;
}

export interface Transport {
  chat: ChatTransportConfig;

  listConversations(args: {
    agentId?: string;
    cursor?: string;
  }): Promise<ConversationsPage>;
  getConversation(id: string): Promise<Conversation>;
  renameConversation(id: string, title: string): Promise<void>;
  deleteConversation(id: string): Promise<void>;

  listAgents(): Promise<Agent[]>;
  getAgent(id: string): Promise<Agent>;
  listStarters(input: ListStartersInput): Promise<ListStartersResponse>;

  uploadFile(
    file: File,
    opts?: { signal?: AbortSignal; conversationId?: string | null }
  ): Promise<UploadedFile>;

  submitFeedback(args: FeedbackPayload): Promise<void>;
  executeAction(args: ActionExecutionPayload): Promise<ActionResult>;
  executeSurfaceAction(input: SurfaceActionInput): Promise<{
    readonly ok: boolean;
    readonly message?: string;
    readonly data?: Record<string, unknown> | null;
    /** A2UI envelopes the handler returned for the client to apply to a
     *  surface (optimistic, in-session). Also persisted BE-side for reload. */
    readonly a2uiMessages?: readonly A2UIEnvelope[];
  }>;
  improvePrompt(args: {
    prompt: string;
    agentId?: string;
    contexts: Context[];
  }): Promise<string>;
  getSlashCommands?(agentId: string): Promise<SlashCommand[]>;
  /** Guesses the user's next prompt after a finished turn, shown as the
   *  composer placeholder (Tab fills it in). Resolves `null` when there is
   *  no suggestion or the request fails. When omitted, no suggestions show. */
  suggestNextPrompt?(
    input: NextPromptInput,
    signal?: AbortSignal
  ): Promise<string | null>;
}

export interface NextPromptInput {
  conversationId: string;
  userMessage: string;
  assistantText: string;
}
